import { Link } from 'react-router-dom';
import type { LessonLog, TestRecord } from '@/types';
import { formatDate } from '@/lib/datetime';

interface LessonCardItemProps {
  lesson: LessonLog;
  studentName?: string;
  tests?: TestRecord[];
  progressUnits?: string[];
  to?: string;
  onDelete?: (id: string) => void;
}

export default function LessonCardItem({
  lesson,
  studentName,
  tests = [],
  progressUnits = [],
  to,
  onDelete,
}: LessonCardItemProps) {
  const d = new Date(lesson.lesson_date);
  const valid = !Number.isNaN(d.getTime());
  const month = valid ? `${d.getMonth() + 1}월` : '-';
  const day = valid ? String(d.getDate()) : '-';

  return (
    <article className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <span className="text-xs font-medium">{month}</span>
          <span className="text-lg font-bold leading-none">{day}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {studentName && (
              <span className="text-sm font-bold text-foreground-950">{studentName}</span>
            )}
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-calendar-line"></i>
              {formatDate(lesson.lesson_date)}
            </span>
            {lesson.is_absent ? (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                <i className="ri-user-unfollow-line"></i>
                결석
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                <i className="ri-check-line"></i>
                출석
              </span>
            )}
            {lesson.is_makeup && (
              <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
                <i className="ri-calendar-schedule-line"></i>
                보충 수업
              </span>
            )}
          </div>

          {lesson.is_absent && lesson.make_up_date && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-foreground-600">
              <i className="ri-calendar-schedule-line"></i>
              보충 예정 {formatDate(lesson.make_up_date)}
            </p>
          )}

          <div className="mt-3 flex flex-col gap-2.5">
            {lesson.lesson_scope && (
              <div>
                <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                  <i className="ri-book-open-line text-primary-600"></i>
                  수업 범위
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                  {lesson.lesson_scope}
                </p>
              </div>
            )}
            {lesson.next_homework && (
              <div>
                <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                  <i className="ri-edit-box-line text-secondary-700"></i>
                  다음 숙제
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                  {lesson.next_homework}
                </p>
              </div>
            )}
            {lesson.homework_check && (
              <div>
                <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                  <i className="ri-checkbox-circle-line text-accent-600"></i>
                  숙제 이행검사
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                  {lesson.homework_check}
                </p>
              </div>
            )}
            {lesson.notes && (
              <div>
                <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                  <i className="ri-sticky-note-line text-foreground-500"></i>
                  특이사항
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                  {lesson.notes}
                </p>
              </div>
            )}
          </div>

          {progressUnits.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-background-200 pt-3">
              <span className="flex items-center gap-1 text-xs font-semibold text-primary-700">
                <i className="ri-route-line"></i>
                완료 진도
              </span>
              {progressUnits.map((u) => (
                <span
                  key={u}
                  className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700"
                >
                  <i className="ri-checkbox-circle-fill"></i>
                  {u}
                </span>
              ))}
            </div>
          )}

          {tests.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-background-200 pt-3">
              {tests.map((t) => (
                <span
                  key={t.id}
                  className="flex items-center gap-1 rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-800"
                >
                  <i className="ri-quill-pen-line"></i>
                  {t.test_scope || '테스트'}
                  {t.difficulty && <span>· 난이도 {t.difficulty}</span>}
                  {t.score && <span className="font-bold">· {t.score}</span>}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {to && (
            <Link
              to={to}
              aria-label="수업일지 수정"
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-primary-700"
            >
              <i className="ri-edit-line"></i>
            </Link>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(lesson.id)}
              aria-label="수업일지 삭제"
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}